import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { ARAB_COUNTRIES, JORDAN_CODE } from './data';

export const getControlErrorMessage$ = (
  control: AbstractControl | null,
  translate: TranslateService
): Observable<string | null> => {
  if (!control || !control.errors) return of(null);

  const errors = control.errors;

  if (errors['required'])
    return translate.stream('FORM_ERRORS.REQUIRED');

  if (errors['minlength'])
    return translate.stream('FORM_ERRORS.MIN_LENGTH', {
      length: errors['minlength'].requiredLength,
    });

  if (errors['maxlength'])
    return translate.stream('FORM_ERRORS.MAX_LENGTH', {
      length: errors['maxlength'].requiredLength,
    });

  if (errors['email'])
    return translate.stream('FORM_ERRORS.EMAIL');

  if (errors['pattern'])
    return translate.stream('FORM_ERRORS.PATTERN');

  if (errors['mismatch'])
    return translate.stream('FORM_ERRORS.MISMATCH');

  if (errors['apiError'])
    return of(errors['apiError']);

  return of(null);
};


/**
 * Date Format Validator
 * Supports formats: DD/MMM/YYYY (10/Oct/2025) or DD/MM/YYYY (10/01/2025)
 */
export function dateFormatValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;

  if (!value) return null;

  const regex = /^(0?[1-9]|[12][0-9]|3[01])[\/\-](?:[A-Za-z]{3}|(0?[1-9]|1[0-2]))[\/\-]\d{4}$/;
  return regex.test(value) ? null : { invalidDateFormat: true };
}

// DOB validator no future date
export function noFutureDateValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) return null;

  let parsedDate: Date | null = null;

  const monthNames: any = {
    Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
    Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11
  };

  // Case 1: DD/Mon/YYYY (10/Oct/2025)
  if (/^[0-9]{1,2}\/[A-Za-z]{3}\/\d{4}$/.test(value)) {
    const [d, m, y] = value.split('/');
    parsedDate = new Date(+y, monthNames[m], +d);
  }

  // Case 2: DD/MM/YYYY (10/01/2025)
  else if (/^[0-9]{1,2}\/[0-9]{1,2}\/\d{4}$/.test(value)) {
    const [d, m, y] = value.split('/');
    parsedDate = new Date(+y, +m - 1, +d);
  }

  if (!parsedDate || isNaN(parsedDate.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (parsedDate > today) {
    return { futureDateNotAllowed: true };
  }

  return null;
}


/**
 * File Type & Size Validator
 * @param allowedExtensions - Allowed file extensions, e.g. ['jpg','png','pdf','doc']
 * @param maxSizeMB - Maximum size in MB
 */
export function fileValidator(allowedExtensions: string[], maxSizeMB: number) {
  return (control: AbstractControl): ValidationErrors | null => {
    const file = control.value;
    if (!file) return null;
    if (typeof file === 'string') return null; // for pre-uploaded references

    const ext = file.name.split('.').pop()?.toLowerCase();
    const sizeMB = file.size / (1024 * 1024);

    if (!ext || !allowedExtensions.includes(ext)) {
      return { invalidFileType: true };
    }
    if (sizeMB > maxSizeMB) {
      return { fileTooLarge: true };
    }
    return null;
  };
}

/**
 * Returns required fields based on nationality
 * @param code - Maximum size in MB
 */
export function getRequiredFieldsByNationality(code: string) {
  if (!code) {
    return { arabicRequired: [], englishRequired: [] };
  }

  // Jordanian → full arabic + english
  if (code === JORDAN_CODE) {
    return {
      arabicRequired: ['firstNameAr', 'fatherNameAr', 'grandFatherNameAr', 'surnameAr'],
      englishRequired: ['firstNameEn', 'fatherNameEn', 'grandFatherNameEn', 'surnameEn']
    };
  }

  // Arab but NOT Jordanian → only arabic required
  if (ARAB_COUNTRIES.includes(code)) {
    return {
      arabicRequired: ['firstNameAr', 'surnameAr'],
      englishRequired: ['firstNameEn', 'surnameEn'] // optional, but you may remove if needed
    };
  }

  // Other countries → only English required
  return {
    arabicRequired: [],
    englishRequired: ['firstNameEn', 'surnameEn']
  };
}

/**
 * Percentage Validator (0–100, allows decimals)
 * Reusable by passing control name.
 */
export function percentageValidator(fieldName?: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === '') return null;

    const num = Number(value);
    if (Number.isNaN(num)) {
      return { invalidPercentage: { field: fieldName } };
    }

    return num >= 0 && num <= 100
      ? null
      : { invalidPercentage: { field: fieldName } };
  };
}


/**
 * Amount Validator (numeric only, allows decimals)
 */
export function amountValidator(fieldName?: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === '') return null;

    const regex = /^[0-9]+(\.[0-9]+)?$/;

    return regex.test(value)
      ? null
      : { invalidAmount: { field: fieldName } };
  };
}

/**
 * Min < Max Validator (Cross-field)
 * Pass field names so validator is reusable anywhere
 */
export function minLessThanMaxValidator(
  minField: string,
  maxField: string
): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const form = group as FormGroup;

    const min = Number(form.get(minField)?.value);
    const max = Number(form.get(maxField)?.value);

    if (!min || !max) return null;

    return min < max
      ? null
      : {
        minGreaterThanMax: {
          minField,
          maxField
        }
      };
  };
}

// Month Validator
export function monthValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) return null;

  const validMonths = [
    'jan', 'feb', 'mar', 'apr', 'may', 'jun',
    'jul', 'aug', 'sep', 'oct', 'nov', 'dec'
  ];

  const value = String(control.value).trim().toLowerCase();

  return validMonths.includes(value)
    ? null
    : { invalidMonth: true };
}

export function getInitials(name: string): string {
  if (!name) return "";

  return name
    .trim()
    .split(/\s+/)                 // split by any number of spaces
    .map(part => part[0])         // take first letter of each word
    .join('')
    .toUpperCase();
}
